/** Safari 26 clips fixed/sticky paint at the browser controls; document paint can continue below. */
export function isNativeSafariBrowser(navigator, standalone = false) {
  const ua = navigator?.userAgent || '';
  return !standalone && /Version\/\d+.*Safari\//.test(ua)
    && (/iP(?:hone|ad|od)/.test(ua) || (navigator?.platform === 'MacIntel' && navigator?.maxTouchPoints > 1))
    && !/CriOS|FxiOS|EdgiOS|YaBrowser|Yowser|OPiOS|GSA|DuckDuckGo/i.test(ua);
}
export function safariFadeGeometry({ scrollY, offsetTop, visibleHeight, largeHeight, baseHeight, minimumTail, documentHeight }) {
  const tail = Math.max(minimumTail, largeHeight - visibleHeight, 0);
  return { top: Math.max(0, scrollY + offsetTop + visibleHeight - baseHeight), tail, documentHeight: Math.max(documentHeight, visibleHeight) };
}
export function trackSafariDocumentFade({ window: win, document: doc, host, fade, probe }) {
  const root = doc.getElementById('__nuxt') || doc.body;
  let frame = 0, stopped = false, lastTail = 0, baseHeight = 88;
  const nativeTimeline = win.CSS?.supports?.('animation-timeline', 'scroll(root block)') === true;
  if (nativeTimeline) host.setAttribute?.('data-scroll-timeline', '');
  const bindings = [];
  const update = () => {
    frame = 0;
    if (stopped) return;
    const measuredHeight = fade.getBoundingClientRect().height;
    if (measuredHeight > 0) baseHeight = Math.max(0, measuredHeight - lastTail);
    const vv = win.visualViewport;
    const scrollY = win.scrollY || 0;
    const minimumTail = parseFloat(win.getComputedStyle(host).getPropertyValue('--uo-layout-mobileBottomNavSafariTail')) || 64;
    const value = safariFadeGeometry({ scrollY, offsetTop: vv?.offsetTop || 0,
      visibleHeight: vv?.height || win.innerHeight,
      largeHeight: Math.max(win.innerHeight, probe.getBoundingClientRect().height),
      baseHeight, minimumTail,
      documentHeight: Math.max(doc.documentElement.clientHeight, root.getBoundingClientRect().bottom + scrollY, doc.body?.getBoundingClientRect?.().bottom + scrollY || 0, ...Array.from(doc.body?.children || []).filter(node => node !== host && !node.classList?.contains('uo-mobile-fade-host') && win.getComputedStyle(node).position !== 'fixed').map(node => node.getBoundingClientRect().bottom + scrollY)) });
    // Clip to the independent app height: the decorative tail must never grow the scroll range.
    host.style.height = value.documentHeight + 'px';
    // Re-measure only after clipping our own decoration, including ad content outside #__nuxt.
    const contentHeight = Math.max(value.documentHeight, (doc.scrollingElement || doc.documentElement).scrollHeight || 0);
    host.style.height = contentHeight + 'px';
    host.style.setProperty('--uo-safari-document-height', contentHeight + 'px');
    host.style.setProperty('--uo-safari-scroll-range', Math.max(0, contentHeight - doc.documentElement.clientHeight) + 'px');
    host.style.setProperty('--uo-safari-fade-top', value.top + 'px');
    host.style.setProperty('--uo-safari-fade-tail', value.tail + 'px');
    lastTail = value.tail;
  };
  const schedule = () => { if (!stopped && !frame) frame = win.requestAnimationFrame(update); };
  const listen = (target, event) => { if (target) { target.addEventListener(event, schedule, { passive: true }); bindings.push([target, event]); } };
  for (const event of nativeTimeline ? ['resize', 'orientationchange'] : ['scroll', 'resize', 'orientationchange']) listen(win, event);
  for (const event of nativeTimeline ? ['resize'] : ['scroll', 'resize']) listen(win.visualViewport, event);
  const observer = win.ResizeObserver ? new win.ResizeObserver(schedule) : null;
  observer?.observe(root);
  const observeBody = () => { if (doc.body) { observer?.observe(doc.body); for (const node of doc.body.children) if (node !== host && !node.classList?.contains('uo-mobile-fade-host')) observer?.observe(node); } };
  observeBody();
  const mutations = win.MutationObserver && doc.body ? new win.MutationObserver(() => { observeBody(); schedule(); }) : null;
  mutations?.observe(doc.body, { childList: true });
  update();
  return () => { stopped = true; if (frame) win.cancelAnimationFrame(frame); for (const [target,event] of bindings) target.removeEventListener(event,schedule); observer?.disconnect(); mutations?.disconnect(); host.removeAttribute?.('data-scroll-timeline'); };
}
